import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import {
  isMailboxEditorSegment,
  mailboxEditorRouteSegments,
  mailboxEditorSegmentForMode,
  type MailboxEditorMode,
} from '../routing/mailbox'

export type MailboxRouteMode = DeskViewMode

const routeSegmentByMode: Record<MailboxRouteMode, string> = {
  msg: '',
  answers: 'inbox',
  submsges: 'messages',
  attachments: 'attachments',
  favs: 'favs',
  drafts: 'drafts',
  trash: 'trash',
}

const modeByRouteSegment: Record<string, MailboxRouteMode> = {
  inbox: 'answers',
  answers: 'answers',
  messages: 'submsges',
  msges: 'submsges',
  submsges: 'submsges',
  attachments: 'attachments',
  favs: 'favs',
  starred: 'favs',
  drafts: 'drafts',
  trash: 'trash',
  deleted: 'trash',
}

const mailboxRouteSegments = new Set([...Object.keys(modeByRouteSegment), ...mailboxEditorRouteSegments])

export function mailboxModeFromSegment(segment: string | undefined): MailboxRouteMode {
  if (!segment) return 'msg'
  return modeByRouteSegment[segment.toLowerCase()] || 'msg'
}

export function mailboxModeFromPathname(pathname: string): MailboxRouteMode {
  const lastSegment = pathname.split('/').filter(Boolean).pop()
  return mailboxModeFromSegment(lastSegment)
}

function mailboxBasePath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length && mailboxRouteSegments.has(parts[parts.length - 1].toLowerCase())) parts.pop()
  return `/${parts.join('/')}` || '/'
}

export function mailboxPathForMode(mode: MailboxRouteMode, pathname: string, search = '', hash = ''): string {
  const base = mailboxBasePath(pathname)
  const segment = routeSegmentByMode[mode]
  const nextPath = segment ? `${base}/${segment}` : base || '/'
  return `${nextPath}${search}${hash}`
}

export function mailboxPathForEditorMode(mode: MailboxEditorMode, pathname: string, search = '', hash = ''): string {
  const base = mailboxBasePath(pathname)
  return `${base}/${mailboxEditorSegmentForMode(mode)}${search}${hash}`
}

export function isMailboxEditorPathname(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean).pop() || ''
  return isMailboxEditorSegment(segment)
}

export function pushMailboxModeUrl(mode: MailboxRouteMode) {
  if (typeof window === 'undefined') return
  const nextUrl = mailboxPathForMode(mode, window.location.pathname, window.location.search, window.location.hash)
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) window.history.pushState({ mailboxMode: mode }, '', nextUrl)
}

export function pushMailboxEditorUrl(mode: MailboxEditorMode) {
  if (typeof window === 'undefined') return
  const nextUrl = mailboxPathForEditorMode(mode, window.location.pathname, window.location.search, window.location.hash)
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) window.history.pushState({ mailboxEditorMode: mode }, '', nextUrl)
}

export function replaceMailboxModeUrl(mode: MailboxRouteMode) {
  if (typeof window === 'undefined') return
  const nextUrl = mailboxPathForMode(mode, window.location.pathname, window.location.search, window.location.hash)
  window.history.replaceState({ mailboxMode: mode }, '', nextUrl)
}

export function applyMailboxMode(mode: MailboxRouteMode, target: HTMLElement | null | undefined, options: { updateUrl?: boolean } = {}) {
  dispatchDeskNavigation({ mode, target: target || undefined })

  if (options.updateUrl) pushMailboxModeUrl(mode)
}
